'use client';
import {
  Button,
  Checkbox,
  Typography,
} from '@curology/ui-components-web/react';
import { FormulaChangeIcon } from '@curology/ui-components-web/react/Icon/icons/FormulaChangeIcon';
import Image from 'next/image';
import { type FormEvent } from 'react';

import { type ProductVariantProduct } from '@/services/resubscriptionService/types';

import { useShopifyCart } from '../shopify-cart/useShopifyCart';

const id = (shopifyVariantId: string) =>
  `gid://shopify/ProductVariant/${shopifyVariantId}`;

const formatPrice = (price: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(price);

const getSubtotal = (
  productVariants: ProductVariantProduct[],
  isVariantSelected: (productVariantId: string) => boolean,
) => {
  let subtotal = 0;

  productVariants.forEach(({ price, shopifyVariantId }) => {
    if (isVariantSelected(id(shopifyVariantId))) {
      subtotal += price;
    }
  });

  return subtotal;
};

const ShopifyResubscribeClient = ({
  productVariants,
}: {
  productVariants: ProductVariantProduct[];
}) => {
  // TODO: Need to reorder the product variants somehow.
  const { isVariantSelected, submit, toggle } = useShopifyCart({
    defaultVariants: productVariants.map((productVariant) =>
      id(productVariant.shopifyVariantId),
    ),
    discountCodes: [],
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    submit();
  };

  const subtotal = getSubtotal(productVariants, isVariantSelected);
  const isBelowMinimumSubtotal = subtotal < 30;

  return (
    <div>
      <Typography size="h2">Resume your subscription</Typography>
      <Typography className="cur-mt-2" size="body">
        Confirm the products you would like below—and we&apos;ll get to work
        mixing your bottle.
      </Typography>
      <form
        className="cur-bg-white cur-mt-6 cur-px-4 cur-py-6"
        onSubmit={onSubmit}
      >
        <ul>
          {productVariants.map(
            ({ blurb, image, name, price, shopifyVariantId }) => {
              return (
                <li
                  className="cur-border-b-2 cur-border-solid cur-border-[#E4E4E4] cur-flex cur-mb-4 cur-pb-4"
                  key={shopifyVariantId}
                >
                  <div className="cur-h-[108px] cur-mr-4 cur-relative cur-w-[86px]">
                    {image && (
                      <Image
                        alt={image.altText ?? `Thumbnail image of ${name}`}
                        layout="fill"
                        objectFit="cover"
                        src={image.url}
                      />
                    )}
                  </div>
                  <div className="cur-flex-1 cur-mr-4">
                    <Typography
                      dangerouslySetInnerHTML={{ __html: name }}
                      size="subhead"
                    ></Typography>
                    <Typography className="cur-mt-2" size="meta" weight="bold">
                      {formatPrice(price)}
                    </Typography>
                    {blurb && (
                      <Typography className="cur-mt-2" size="meta">
                        {blurb}
                      </Typography>
                    )}
                  </div>
                  <div>
                    <Checkbox
                      checked={isVariantSelected(id(shopifyVariantId))}
                      id={shopifyVariantId}
                      name="shopifyVariantId[]"
                      onChange={() =>
                        toggle({
                          id: id(shopifyVariantId),
                        })
                      }
                      tabIndex={0}
                    />
                  </div>
                </li>
              );
            },
          )}
        </ul>
        <div className="cur-bg-curology-brand-50 cur-flex cur-items-center cur-justify-between cur-mt-6 cur-px-4 cur-py-7">
          <Typography>Subtotal</Typography>
          <Typography weight="bold">{formatPrice(subtotal)}</Typography>
        </div>
        <div className="cur-mt-6">
          <Button
            className="cur-w-full"
            disabled={isBelowMinimumSubtotal}
            type="submit"
          >
            Proceed to checkout
          </Button>
          {isBelowMinimumSubtotal && (
            <Typography className="cur-mt-2 cur-text-center" weight="bold">
              Minimum order is $30
            </Typography>
          )}
        </div>
      </form>
      <div className="cur-flex cur-items-center cur-justify-center cur-mt-6">
        <FormulaChangeIcon className="cur-mr-4" />
        <Typography>Edit your box or cancel anytime</Typography>
      </div>
    </div>
  );
};

export { ShopifyResubscribeClient };
